import React, { useState } from 'react'
import { Field } from 'formik'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, IconButton } from '@mui/material'
import { Edit } from '@mui/icons-material'
import * as Yup from 'yup'
import { useToasts } from 'react-toast-notifications'

import { CustomTextField, RequestForm } from 'src/form'
import CustomMuiThemeProvider from 'src/designSystem/CustomMuiThemeProvider'
import { ClosableDialogTitle } from 'src/legacy/components/mui'

type Props = {
  coachID: string
  coachName: string
  currentEmail: string
  onSubmit: (coachID: string, email: string) => Promise<unknown>
}

// Factory so we can reject an unchanged email against the coach's current address.
// The server normalizes with trim().toLowerCase() (see emailSchema), so compare
// normalized values here or a differently-cased duplicate slips through as a no-op.
const normalizeEmail = (email: string) => email.trim().toLowerCase()

const makeSchema = (currentEmail: string) =>
  Yup.object()
    .required()
    .shape({
      email: Yup.string()
        .email()
        .required()
        .test('different-from-current', 'Enter an email different from the current one', (value) =>
          value == null ? true : normalizeEmail(value) !== normalizeEmail(currentEmail)
        ),
    })

const EditCoachEmailButton: React.FunctionComponent<Props> = ({ coachID, coachName, currentEmail, onSubmit }) => {
  const [open, setOpen] = useState(false)
  const { addToast } = useToasts()
  const onClose = () => setOpen(false)

  return (
    <>
      <IconButton onClick={() => setOpen(true)} size="medium">
        <Edit titleAccess="Edit email" />
      </IconButton>
      <CustomMuiThemeProvider>
        <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
          <ClosableDialogTitle onClose={onClose}>Change coach email</ClosableDialogTitle>
          <RequestForm
            initialValues={{ email: '' }}
            resetAfterSuccess={false}
            validationSchema={makeSchema(currentEmail)}
            onSubmit={({ email }) => onSubmit(coachID, email)}
            onSuccess={({ email }) => {
              addToast(`Email updated to ${email}`, { appearance: 'success' })
              onClose()
            }}
            render={({ isSubmitting }) => (
              <>
                <DialogContent>
                  <DialogContentText color="textPrimary" gutterBottom>
                    Update the Google Workspace email for {coachName} (currently {currentEmail}). The new address must
                    already exist in Google Workspace, or the change will be rejected.
                  </DialogContentText>
                  <Field autoFocus component={CustomTextField} fullWidth label="New email" name="email" />
                </DialogContent>
                <DialogActions>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
                    Save
                  </Button>
                </DialogActions>
              </>
            )}
          />
        </Dialog>
      </CustomMuiThemeProvider>
    </>
  )
}

export default EditCoachEmailButton
